/**
 * The Composer's live mix engine (Dax, 2026-09-29). Adjusting a level used to queue a server render of
 * the whole cut, 20 to 30 seconds for eight seconds of video; now the browser mixes. Rough by design,
 * the way the picture preview is cut from proxies: Normalise is left to Export, and compression and EQ
 * are the Web Audio twins of the render's own (the same presets and shelves).
 *
 * The graph: each preview player's own sound (MediaElementSource, one per element for its life) passes
 * a per-slot gain into the clips bus; every other layer is a decoded buffer scheduled on the cut's
 * timeline into its channel's bus. Each bus has its level and a meter; they sum into the master chain.
 * Media must come with CORS for Web Audio to hear it, so every URL it reads carries `MIX_QUERY`, a
 * cache key of its own: a copy cached earlier without CORS headers can never answer for it.
 */
import { AUDIO_CHANNELS, AUDIO_RAMP_MS, BASS_SHELF_HZ, COMPRESSION_PRESETS, TREBLE_SHELF_HZ, type AudioChannel, type AudioFx, type ChannelLevel } from '@studio/contracts';
import { firstSignalMs, measureLoudness, type Loudness } from '@/lib/level';
import { dbToGain, soundWindow, type MixPlan, type MixSound, type MixStem } from '@/lib/mix-plan';

/** The query that keeps mixed media in a cache entry of its own. */
const MIX_QUERY = 'mix=1';
/** The render's ramp at a layer's cut edges, so the preview never clicks where Export does not. */
const RAMP_S = AUDIO_RAMP_MS / 1000;
/** Scheduling lead: sources start this far ahead so the first one never lands in the past. */
const START_LEAD_S = 0.03;
/** The meter's analysis window. */
const METER_FFT = 2048;
/** Below this a reading is silence, not a level. */
const METER_FLOOR_DB = -90;

export function mixUrl(url: string): string {
  return url.includes('?') ? `${url}&${MIX_QUERY}` : `${url}?${MIX_QUERY}`;
}

type Meter = { analyser: AnalyserNode; buf: Float32Array<ArrayBuffer>; sumSq: number; count: number; peak: number };

export class PreviewMix {
  private ctx: AudioContext | null = null;
  private buses = new Map<AudioChannel, GainNode>();
  private meters = new Map<AudioChannel | 'master', Meter>();
  private compressor: DynamicsCompressorNode | null = null;
  /** Compression is a switch between two paths: through the compressor, or straight past it. */
  private compressed: GainNode | null = null;
  private bypass: GainNode | null = null;
  private bass: BiquadFilterNode | null = null;
  private treble: BiquadFilterNode | null = null;
  private master: GainNode | null = null;
  private elements = new WeakMap<HTMLMediaElement, GainNode>();
  private buffers = new Map<string, Promise<AudioBuffer | null>>();
  private running: AudioBufferSourceNode[] = [];
  private raf: number | null = null;

  /** Build the graph on the first play (a click, so the browser lets it sound) and wake it. */
  ensure(): AudioContext {
    if (!this.ctx) {
      const ctx = new AudioContext();
      this.ctx = ctx;
      this.bass = new BiquadFilterNode(ctx, { type: 'lowshelf', frequency: BASS_SHELF_HZ, gain: 0 });
      this.treble = new BiquadFilterNode(ctx, { type: 'highshelf', frequency: TREBLE_SHELF_HZ, gain: 0 });
      this.compressor = new DynamicsCompressorNode(ctx);
      this.compressed = new GainNode(ctx, { gain: 0 });
      this.bypass = new GainNode(ctx);
      const sum = new GainNode(ctx);
      sum.connect(this.compressor);
      sum.connect(this.bypass);
      this.compressor.connect(this.compressed);
      this.compressed.connect(this.bass);
      this.bypass.connect(this.bass);
      this.master = new GainNode(ctx);
      this.bass.connect(this.treble);
      this.treble.connect(this.master);
      this.master.connect(ctx.destination);
      this.meters.set('master', this.meter(this.master));
      for (const ch of AUDIO_CHANNELS) {
        const bus = new GainNode(ctx);
        bus.connect(sum);
        this.buses.set(ch, bus);
        this.meters.set(ch, this.meter(bus));
      }
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
    return this.ctx;
  }

  private meter(node: AudioNode): Meter {
    const analyser = new AnalyserNode(this.ctx!, { fftSize: METER_FFT });
    node.connect(analyser);
    return { analyser, buf: new Float32Array(METER_FFT), sumSq: 0, count: 0, peak: 0 };
  }

  /** Route a preview player's own sound through the clips bus. An element can be attached only once. */
  attach(el: HTMLMediaElement): void {
    const ctx = this.ensure();
    if (this.elements.has(el)) return;
    const gain = new GainNode(ctx);
    ctx.createMediaElementSource(el).connect(gain);
    gain.connect(this.buses.get('clips')!);
    this.elements.set(el, gain);
  }

  /** One player's level for the slot it is about to play. */
  setElementGain(el: HTMLMediaElement, db: number | null): void {
    const gain = this.elements.get(el);
    if (gain && this.ctx) gain.gain.setValueAtTime(dbToGain(db), this.ctx.currentTime);
  }

  /**
   * A whole-cut file (the text preview) plays every slot through one player: its level steps at each
   * slot's start, scheduled from where playback begins.
   */
  scheduleElementGains(el: HTMLMediaElement, plan: MixPlan, fromMs: number): void {
    const gain = this.elements.get(el);
    if (!gain || !this.ctx) return;
    const now = this.ctx.currentTime;
    gain.gain.cancelScheduledValues(now);
    for (const s of plan.slots) {
      if (s.endMs <= fromMs) continue;
      gain.gain.setValueAtTime(dbToGain(s.gainDb), now + Math.max(0, s.startMs - fromMs) / 1000);
    }
  }

  /** Levels, mutes and the master chain, applied live: a slider moves the sound while it plays. */
  apply(plan: MixPlan): void {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    for (const ch of AUDIO_CHANNELS) this.buses.get(ch)!.gain.setTargetAtTime(dbToGain(plan.buses[ch]), now, 0.01);
    const compression = plan.fx.compression;
    if (compression !== 'off') {
      const p = COMPRESSION_PRESETS[compression];
      this.compressor!.threshold.value = p.thresholdDb;
      this.compressor!.ratio.value = p.ratio;
      this.compressor!.attack.value = p.attackMs / 1000;
      this.compressor!.release.value = p.releaseMs / 1000;
    }
    this.compressed!.gain.value = compression === 'off' ? 0 : 1;
    this.bypass!.gain.value = compression === 'off' ? 1 : 0;
    this.bass!.gain.value = plan.fx.bassDb;
    this.treble!.gain.value = plan.fx.trebleDb;
    this.master!.gain.setTargetAtTime(dbToGain(plan.masterGainDb), now, 0.01);
  }

  /** Fetch and decode every layer the plan will play, so pressing play is instant. */
  preload(plan: MixPlan): void {
    for (const l of this.layersOf(plan)) void this.buffer(l.sound.layer.url);
  }

  private layersOf(plan: MixPlan): { sound: MixSound; bus: AudioChannel }[] {
    return [
      ...plan.sounds.map((sound) => ({ sound, bus: 'asmr' as const })),
      ...plan.music.map((sound) => ({ sound, bus: 'music' as const })),
      ...plan.voice.map((sound) => ({ sound, bus: 'voice' as const })),
      ...plan.soundtrack.map((sound) => ({ sound, bus: 'soundtrack' as const })),
    ];
  }

  private buffer(url: string): Promise<AudioBuffer | null> {
    let hit = this.buffers.get(url);
    if (!hit) {
      // Decoding needs a context but not a click: one made before the first play stays suspended until then.
      const ctx = this.ensure();
      hit = fetch(mixUrl(url), { mode: 'cors' })
        .then((r) => {
          if (!r.ok) throw new Error(`${r.status}`);
          return r.arrayBuffer();
        })
        .then((bytes) => ctx.decodeAudioData(bytes))
        .catch((err: unknown) => {
          // A layer that will not load plays as silence; the rest of the mix still plays. Said once.
          console.warn('preview mix: a layer would not load', url, err);
          this.buffers.delete(url);
          return null;
        });
      this.buffers.set(url, hit);
    }
    return hit;
  }

  /**
   * A layer's waveform at any resolution, read from the audio the mix already decoded: a full-length
   * file's stored card waveform is too coarse to drag a 44-second song under an eight-second cut by.
   * `points` bars over the stretch `fromMs` to `fromMs + durationMs`, each the loudest sample in it.
   */
  async peaks(url: string, fromMs: number, durationMs: number, points: number): Promise<number[] | null> {
    const buf = await this.buffer(url);
    if (!buf || points <= 0) return null;
    const data = buf.getChannelData(0);
    const from = Math.floor((fromMs / 1000) * buf.sampleRate);
    const span = Math.max(1, Math.floor((durationMs / 1000) * buf.sampleRate));
    const out: number[] = [];
    for (let i = 0; i < points; i++) {
      const a = from + Math.floor((i * span) / points);
      const b = Math.min(data.length, from + Math.floor(((i + 1) * span) / points));
      let peak = 0;
      for (let j = a; j < b; j++) {
        const v = Math.abs(data[j]!);
        if (v > peak) peak = v;
      }
      out.push(peak);
    }
    return out;
  }

  /**
   * How loud a stretch of a file is, for levelling: decoded once and shared with playback. A clip's own
   * sound is read from its proxy the same way; a file with no sound track measures as null.
   */
  async measure(url: string, fromMs: number, durationMs: number): Promise<Loudness | null> {
    const buf = await this.buffer(url);
    if (!buf) return null;
    const channels = Array.from({ length: buf.numberOfChannels }, (_, c) => buf.getChannelData(c));
    return measureLoudness(channels, buf.sampleRate, fromMs, durationMs);
  }

  /**
   * How loud the whole cut is before the master, without playing it: every stem summed through the master's EQ
   * and compression, faster than real time and out of earshot. Null when nothing plays.
   */
  async measureMix(stems: MixStem[], totalMs: number, fx: AudioFx): Promise<Loudness | null> {
    if (!stems.length || totalMs <= 0) return null;
    const { sampleRate } = this.ensure();
    const buffers = await Promise.all(stems.map((s) => this.buffer(s.url)));
    const off = new OfflineAudioContext(2, Math.ceil((totalMs / 1000) * sampleRate), sampleRate);
    const sum = new GainNode(off);
    const bass = new BiquadFilterNode(off, { type: 'lowshelf', frequency: BASS_SHELF_HZ, gain: fx.bassDb });
    const treble = new BiquadFilterNode(off, { type: 'highshelf', frequency: TREBLE_SHELF_HZ, gain: fx.trebleDb });
    if (fx.compression === 'off') sum.connect(bass);
    else {
      const p = COMPRESSION_PRESETS[fx.compression];
      sum.connect(new DynamicsCompressorNode(off, { threshold: p.thresholdDb, ratio: p.ratio, attack: p.attackMs / 1000, release: p.releaseMs / 1000 })).connect(bass);
    }
    bass.connect(treble).connect(off.destination);
    stems.forEach((s, i) => {
      const buffer = buffers[i];
      if (!buffer) return;
      const src = new AudioBufferSourceNode(off, { buffer });
      src.connect(new GainNode(off, { gain: dbToGain(s.gainDb) })).connect(sum);
      src.start(s.atMs / 1000, s.fromMs / 1000, s.durationMs / 1000);
    });
    const out = await off.startRendering();
    return measureLoudness([out.getChannelData(0), out.getChannelData(1)], sampleRate, 0, totalMs);
  }

  /** Where a file's sound begins, in ms, decoded once and shared with playback; null when it never rises above `floorDb`. */
  async firstSignal(url: string, floorDb: number): Promise<number | null> {
    const buf = await this.buffer(url);
    if (!buf) return null;
    return firstSignalMs(Array.from({ length: buf.numberOfChannels }, (_, c) => buf.getChannelData(c)), buf.sampleRate, floorDb);
  }

  /** Play the plan's layers for the stretch of the cut from `fromMs` to `untilMs`, starting now. */
  async start(plan: MixPlan, fromMs: number, untilMs: number): Promise<void> {
    const ctx = this.ensure();
    this.stop();
    this.apply(plan);
    for (const m of this.meters.values()) {
      m.sumSq = 0;
      m.count = 0;
      m.peak = 0;
    }
    const layers = this.layersOf(plan);
    const buffers = await Promise.all(layers.map((l) => this.buffer(l.sound.layer.url)));
    const t0 = ctx.currentTime + START_LEAD_S;
    layers.forEach((l, i) => {
      const buf = buffers[i];
      const w = soundWindow(l.sound, fromMs, untilMs);
      if (!buf || !w) return;
      const src = new AudioBufferSourceNode(ctx, { buffer: buf });
      const level = dbToGain(l.sound.gainDb);
      const gain = new GainNode(ctx, { gain: level });
      src.connect(gain);
      gain.connect(this.buses.get(l.bus)!);
      const at = t0 + w.delayMs / 1000;
      const end = at + w.lengthMs / 1000;
      // A full-length layer is cut at both ends of what plays, so it ramps there as Export does. A
      // clip's sound effect keeps its own attack, unless sharp mode cut it to its clip: then it plays
      // the ramps the plan gives it, timed from the sound's own start and end on the cut.
      if (l.bus !== 'asmr') {
        gain.gain.setValueAtTime(0, at);
        gain.gain.linearRampToValueAtTime(level, at + RAMP_S);
        gain.gain.setValueAtTime(level, Math.max(at + RAMP_S, end - RAMP_S));
        gain.gain.linearRampToValueAtTime(0, end);
      } else if (l.sound.fadeInMs || l.sound.fadeOutMs) {
        const soundStart = t0 + (l.sound.atMs - fromMs) / 1000;
        const soundEnd = soundStart + l.sound.layer.durationMs / 1000;
        if (l.sound.fadeInMs && soundStart >= at) {
          gain.gain.setValueAtTime(0, soundStart);
          gain.gain.linearRampToValueAtTime(level, soundStart + l.sound.fadeInMs / 1000);
        }
        if (l.sound.fadeOutMs && soundEnd > at) {
          gain.gain.setValueAtTime(level, Math.max(at, soundEnd - l.sound.fadeOutMs / 1000));
          gain.gain.linearRampToValueAtTime(0, soundEnd);
        }
      }
      src.start(at, w.offsetMs / 1000, w.lengthMs / 1000);
      this.running.push(src);
    });
    this.watch();
  }

  stop(): void {
    for (const src of this.running) {
      try {
        src.stop();
      } catch {
        // Never started or already ended: nothing to stop.
      }
      src.disconnect();
    }
    this.running = [];
    if (this.raf !== null) cancelAnimationFrame(this.raf);
    this.raf = null;
  }

  /** Meter every channel while the mix plays: running energy for the mean, the loudest sample for the peak. */
  private watch(): void {
    const tick = () => {
      for (const m of this.meters.values()) {
        m.analyser.getFloatTimeDomainData(m.buf);
        let sumSq = 0;
        let peak = 0;
        for (const v of m.buf) {
          sumSq += v * v;
          const a = Math.abs(v);
          if (a > peak) peak = a;
        }
        m.sumSq += sumSq;
        m.count += m.buf.length;
        if (peak > m.peak) m.peak = peak;
      }
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  /** What the meters have heard since playback began: mean and peak per channel, null where silent. */
  levels(): Partial<Record<AudioChannel | 'master', ChannelLevel | null>> {
    const out: Partial<Record<AudioChannel | 'master', ChannelLevel | null>> = {};
    for (const [ch, m] of this.meters) {
      const meanDb = m.count ? 10 * Math.log10(m.sumSq / m.count) : -Infinity;
      const peakDb = m.peak > 0 ? 20 * Math.log10(m.peak) : -Infinity;
      out[ch] = peakDb > METER_FLOOR_DB ? { meanDb: Math.round(meanDb * 10) / 10, peakDb: Math.round(peakDb * 10) / 10 } : null;
    }
    return out;
  }

  close(): void {
    this.stop();
    void this.ctx?.close();
    this.ctx = null;
  }
}
